import {
  assertInstance,
  notNullish,
  parseBoolean,
  parseJsonObject,
  parseNumber,
  parseRecordAs,
  parseRecordValuesAs,
  parseString,
} from "@de-xima/xima-common-js-lang";

import { byCssClass } from "../common/dom-util.js";
import { findWidgetByElement, findWidgetByVar } from "../common/primefaces-util.js";
import { byCssAs, byCssHtml, byId } from "@de-xima/xima-common-js-dom";
import { hideLoadingDialog, showLoadingDialog } from "../components/loading-dialog.js";
import { appendParameterToUrl } from "../common/form-util.js";
import { startPollingDelayed } from "../common/polling-util.js";
import { focusFirstFocusable } from "../common/accessibility.js";

let verticalScroll = 0;

const TriggerResponseFrame = {
  name: "triggerResponseFrame",
  styleClass: "triggerResponseFrame",
  hideCssClass: "hideFrame",
  dialogWidgetVar: "triggerResponseDialog",
  responseFormIdSelector: "#triggerResponseForm",
  hiddenInputJsonResponse: "restApiJsonResponse",
};

export const TabNames = {
  MESSAGE: "MESSAGE",
  FILE: "FILE",
  HISTORY: "HISTORY",
};

/**
 *
 * @param {KeyboardEvent} e
 * @returns
 */
export function blockEnterUnlessButton(e) {
  if (e.key === "Enter") {
    const target = e.target;
    if (
      target instanceof HTMLElement &&
      (target.tagName.toLowerCase() === "button" ||
        target.tagName.toLowerCase() === "a" ||
        (target.tagName.toLowerCase() === "input" && target instanceof HTMLInputElement && target.type === "button"))
    ) {
      return true;
    }

    e.preventDefault();
    return false;
  }
  return true;
}

/**
 * Adds all event listeners for the form record layout.
 */
export const initFormRecordsLayout = () => {
  const formRecordList = byCssClass("form-record__datatable");
  if (formRecordList) {
    startPollingDelayed("pollFormRecordListUpdate", 5000);
  }
  const triggerResponseIframe = byCssClass(TriggerResponseFrame.styleClass);
  if (triggerResponseIframe instanceof HTMLIFrameElement) {
    triggerResponseIframe.addEventListener("load", (_evt) => {
      if (!triggerResponseIframe.classList.contains(TriggerResponseFrame.hideCssClass)) {
        showTriggerRequestDialog();
        toggleFrameVisibility(false);
      }
    });
  }
};

export const startFormRecordPolling = () => {
  const formRecordList = byCssClass("form-record__tab-view");
  if (formRecordList) {
    startPollingDelayed("pollSelectedFormRecord", 5000);
  }
};

export function showFormRecordsLoadingIcon() {
  const loadingIcon = byCssClass("form-records-loading-wrapper");
  if (loadingIcon instanceof HTMLElement) {
    loadingIcon.style.display = "block";
  }
}

export function hideFormRecordsLoadingIcon() {
  const loadingIcon = byCssClass("form-records-loading-wrapper");
  if (loadingIcon instanceof HTMLElement) {
    loadingIcon.style.display = "none";
  }
}

/**
 *
 * @param {string} triggerUuid
 * @param {string} triggerName
 * @param {string} taskUuid
 * @param {string} formRecordUuid
 */
export function receiveTriggerParameter(triggerUuid, taskUuid, formRecordUuid, triggerName) {
  callSendWorkflowTriggerParameters(triggerUuid, taskUuid, formRecordUuid, triggerName);
}

/**
 * When the user clicks on a form record item in the list, this function is called.
 * @param event {Event} the click event.
 * @param tabName {TabNames} the name of the tab to open. Can be "MESSAGE", "FILE" or "HISTORY".
 */
export function onFormRecordItemClick(event, tabName) {
  assertInstance(event.target, HTMLElement);
  const widget = event.target.closest(".ui-widget.ui-datascroller");
  const column = event.target.closest(".form-record__column");
  if (column instanceof HTMLElement && widget instanceof HTMLElement && widget.classList.contains("ui-datascroller")) {
    callBackToFormRecordSelectRemoteCommand(
      parseString(column.dataset.uuid),
      parseString(tabName.valueOf()),
      parseString(true),
    );
  }
  verticalScroll = byId("content")?.scrollTop ?? 0;
}

/**
 * When the user clicks on a row of the form record list. Selects the form record
 * and shows details for the selected form record.
 * @param {Event} event the click event.
 */
export function onFormRecordRowClick(event) {
  assertInstance(event.target, HTMLElement);
  const widget = event.target.closest(".ui-widget");
  const column = event.target.closest(".form-record__column");
  if (column instanceof HTMLElement && widget instanceof HTMLElement && widget.classList.contains("ui-datascroller")) {
    callBackToFormRecordSelectRemoteCommand(parseString(column.dataset.uuid), parseString(""), parseString(true));
  }
  verticalScroll = byId("content")?.scrollTop ?? 0;
}

/**
 * Update the unread messages count in the form record page.
 */
export function updateUnreadCount() {
  const notificationHiddenInput = byCssAs("#headerForm\\:hidden-unread-count", HTMLInputElement);
  if (notificationHiddenInput === undefined) {
    return;
  }
  const notificationNumber = parseNumber(notificationHiddenInput.value);
  const notification = byCssHtml(".form-record-notification__count");
  const notificationMobile = byCssHtml(".form-record-notification__count--mobile");

  if (notification !== undefined) {
    if (notificationNumber === 0) {
      notification.remove();
      return;
    }
    notification.innerText = notificationHiddenInput.value;
  } else {
    if (notificationNumber > 0) {
      const option2 = byCssHtml("#formRecordsForm\\:formRecordFilter\\:1");
      if (option2 !== undefined) {
        const span = document.createElement("span");
        span.className = "form-record-notification__count";
        span.textContent = notificationHiddenInput.value;
        span.setAttribute("aria-hidden", "true");
        option2?.after(span);
      }
    }
  }

  if (notificationMobile !== undefined) {
    if (notificationNumber === 0) {
      notificationMobile.remove();
      return;
    }
    notificationMobile.innerText = notificationHiddenInput.value;
  } else {
    if (notificationNumber > 0) {
      const optionMobile = document.querySelector(
        ".form-record-filter__panel-select.ui-selectcheckboxmenu-panel .ui-selectcheckboxmenu-list .ui-selectcheckboxmenu-item:nth-child(2)",
      );
      if (optionMobile !== undefined) {
        const spanMobile = document.createElement("span");
        spanMobile.className = "form-record-notification__count--mobile";
        spanMobile.textContent = notificationHiddenInput.value;
        spanMobile.setAttribute("aria-hidden", "true");
        optionMobile?.appendChild(spanMobile);
      }
    }
  }
}

/**
 * Restores the vertical scroll position of the form record list. When the user select a form
 * record, its details are shown. When the user click on the back button, the view switches
 * back to the form record list. This function restores the vertical scroll position of the list
 * from before the user clicked on the form record.
 */
export function restoreVerticalScroll() {
  const scrollContainer = byId("content");
  if (scrollContainer !== undefined) {
    scrollContainer.scrollTop = verticalScroll;
  }
}

/**
 * @param {string} uuid
 */
export function markAsRead(uuid) {
  if (uuid) {
    const entry = byCssClass(`row-${uuid}`);
    if (entry !== undefined) {
      entry.classList.remove("entry--new");
    }
  }
}

/**
 * Execute the trigger request.
 * @param {PrimeFaces.ajax.PrimeFacesArgs} args The PrimeFaces arguments object with the data received from the
 * bean.
 */
export const executeTrigger = async (args) => {
  showLoadingDialog();
  const data = parseJsonObject(args.triggerRequestData);
  const url = parseString(data.triggerRequestUrl);
  const cookieName = parseString(data.cookieName);
  const contextPath = parseString(data.contextPath);
  const triggerName = parseString(data.triggerName);
  const apiUrl = parseString(data.apiUrl);
  const formRecordUuid = parseString(data.formRecordUuid);
  const authHeader = parseRecordValuesAs(data.authHeader, parseString);
  const portalAlias = parseString(data.portalAlias);

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: authHeader,
    });
    const jsonResponse = await response.json();
    const form = document.querySelector(TriggerResponseFrame.responseFormIdSelector);

    if (form instanceof HTMLFormElement) {
      prepareFormForSubmission(form, url, jsonResponse, cookieName, contextPath);
      await waitForTriggerResponseCompletionCookie(cookieName, contextPath);
      checkRedirectToForm(jsonResponse, portalAlias);
    }
    await callUpdateFormRecordRowRemoteCommand(formRecordUuid);
    callSuccessGrowlRemoteCommand(triggerName);
  } catch (error) {
    console.error("Error executing POST request:", error);
    hideTriggerRequestDialog();
    callErrorGrowlRemoteCommand(triggerName);
  } finally {
    hideLoadingDialog();
  }
};

/**
 * Open the split button menu when the user click on the left part of a split button.
 * Normally, the default action is executed, and the menu with alternative actions
 * only opens when the user clicks on the down arrow on the right side of the button.
 *
 * We want the menu to open irrespective of where the user clicks on the button.
 * @param {Event} event
 */
export function openSplitButtonMenu(event) {
  if (event instanceof MouseEvent === false) {
    return;
  }
  assertInstance(event.target, HTMLElement);
  const splitButton = findWidgetByElement(event.target, PrimeFaces.widget.SplitButton);
  if (splitButton) {
    const button = event.target.closest(".ui-button");
    const menuButton = button?.parentElement?.querySelector(".ui-splitbutton-menubutton");
    if (button && menuButton && Array.from(button.parentElement?.children ?? []).indexOf(menuButton) !== -1) {
      splitButton?.menuButton.trigger("click");
      return;
    }
  }
}

/**
 *
 * @param {HTMLElement} container
 */
export function focusFirstWorkflowTriggerButton(container) {
  focusFirstFocusable(container);
}

/**
 * Updates the marked form record row. Only one row can be marked at a time.
 * Unmarks all form record rows and marks the row with the given UUID.
 * @param {string} uuid The UUID of the form record row to mark.
 */
export function updateMarkedFormRecordRow(uuid) {
  const dataScroller = findWidgetByVar("formRecords", PrimeFaces.widget.DataScroller);
  if (dataScroller === undefined) {
    console.error("DataScroller widget not found.");
    return;
  }
  const columns = dataScroller.list.find(".form-record__column").toArray();
  for (const column of columns) {
    column.classList.remove("form-record__column--marked");
  }
  columns.filter((x) => x.dataset.uuid === uuid)[0]?.classList.add("form-record__column--marked");
}

/**
 * Pushes the given URL to the history state, allowing the user to navigate back to this state.
 * @param {string} url The URL to select the form record link.
 */
export function pushState(url) {
  history.pushState({}, "", url);
}

/**
 * Selects the form record link by pushing the URL to the history state.
 * @param {string} url The URL to select the form record link.
 */
export function selectFormRecordLink(url) {
  pushState(url);
}

/**
 * Adds a popstate event listener to the window to handle navigation.
 */
export function addFormRecordPopstateListener() {
  window.addEventListener("popstate", (_event) => {
    const currentUrl = window.location.href;
    if (currentUrl.includes("/form-records")) {
      restoreVerticalScroll();
      callBackToFormRecordsRemoteCommand();
    }
    if (currentUrl.includes("/form-record/")) {
      const formRecordUuid = extractUuidFromUrl(currentUrl);
      callBackToFormRecordSelectRemoteCommand(formRecordUuid, parseString(0), parseString(false));
    }
  });
}

/**
 * Extracts the UUID from a URL that contains a form record link.
 * @param url {string} The URL to extract the UUID from.
 * @returns {string} The extracted UUID or null if the URL does not contain a form record link.
 */
function extractUuidFromUrl(url) {
  const marker = "form-record/";
  const index = url.indexOf(marker);
  if (index === -1) {
    return "";
  }
  return url.substring(index + marker.length);
}

/**
 * Prepare the form for submission by setting its attributes and adding the JSON response as a hidden input.
 * @param {HTMLFormElement} form The form element.
 * @param {string} url The URL to set as the form action.
 * @param {unknown} jsonResponse The JSON response.
 * @param {string} cookieName The name of the cookie to delete.
 * @param {string} contextPath The context path for the cookie.
 */
function prepareFormForSubmission(form, url, jsonResponse, cookieName, contextPath) {
  form.action = url;
  form.method = "POST";
  form.target = TriggerResponseFrame.name;

  checkTriggerResponseFrameVisibility(jsonResponse);

  PrimeFaces.deleteCookie(cookieName, { path: contextPath });

  const existingInput = form.querySelector(`#${TriggerResponseFrame.hiddenInputJsonResponse}`);
  if (existingInput) {
    form.removeChild(existingInput);
  }

  const input = document.createElement("input");
  input.type = "hidden";
  input.id = TriggerResponseFrame.hiddenInputJsonResponse;
  input.name = TriggerResponseFrame.hiddenInputJsonResponse;
  input.value = JSON.stringify(jsonResponse);
  form.appendChild(input);

  form.submit();
}

/**
 * Checks the visibility of the trigger response frame based on the JSON response.
 * The frame is hidden if the response status code is 204 or if the forceDownload flag is set to true
 * @param {unknown} jsonResponse The JSON response object.
 */
function checkTriggerResponseFrameVisibility(jsonResponse) {
  const parsed = parseRecordAs(jsonResponse, {
    statusCode: parseNumber,
    forceDownload: parseBoolean,
  });
  const shouldShowFrame = notNullish(jsonResponse) && parsed.statusCode !== 204 && !parsed.forceDownload;
  toggleFrameVisibility(shouldShowFrame);
}

function showTriggerRequestDialog() {
  const dialog = PF(TriggerResponseFrame.dialogWidgetVar);
  if (dialog instanceof PrimeFaces.widget.Dialog) {
    dialog.show();
  }
}

function hideTriggerRequestDialog() {
  const dialog = PF(TriggerResponseFrame.dialogWidgetVar);
  if (dialog instanceof PrimeFaces.widget.Dialog) {
    dialog.hide();
  }
}

/**
 *  Wait for the trigger response completion cookie to be set. The cookie is set by the server when the response is ready.
 * @param {string} cookieName The name of the cookie to wait for.
 * @param {string} path The path of the cookie.
 * @returns {Promise<void>} A promise that resolves when the cookie is set.
 */
function waitForTriggerResponseCompletionCookie(cookieName, path) {
  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      if (PrimeFaces.getCookie(cookieName) === "true") {
        clearInterval(interval);
        clearTimeout(timeout);
        PrimeFaces.deleteCookie(cookieName, { path });
        resolve();
      }
    }, 100);
    const timeout = setTimeout(
      () => {
        clearInterval(interval);
        clearTimeout(timeout);
        reject(new Error("Timeout waiting for response cookie"));
      },
      10 * 60 * 1000,
    );
  });
}

/**
 * Toggles the visibility of the trigger response iframe.
 * @param {boolean} showFrame If true, the frame will be shown, otherwise it will be hidden.
 */
function toggleFrameVisibility(showFrame) {
  const triggerResponseIframe = byCssClass(TriggerResponseFrame.styleClass);
  if (triggerResponseIframe instanceof HTMLIFrameElement) {
    if (showFrame) {
      triggerResponseIframe.classList.remove(TriggerResponseFrame.hideCssClass);
    } else {
      triggerResponseIframe.classList.add(TriggerResponseFrame.hideCssClass);
    }
  }
}

/**
 * Checks if the JSON response contains a redirect URL to a form and submits the form in an IFRAME.
 * @param {{ statusCode: number; redirectUrl: string; }} jsonResponse
 * @param {string} portalAlias
 */
function checkRedirectToForm(jsonResponse, portalAlias) {
  if (!jsonResponse) {
    return;
  }
  if (jsonResponse.statusCode !== 302 || !jsonResponse.redirectUrl) {
    return;
  }
  const redirectUrl = jsonResponse.redirectUrl;
  const isForm = isFcForm(redirectUrl);
  if (isForm) {
    submitFormInIframe(redirectUrl, portalAlias);
  }
}

/**
 * Checks if the redirect URL is from the same domain and contains /form/provide/.
 * @param {string} redirectUrl The URL to check.
 * @returns {boolean} True if the URL is from the same domain and contains /form/provide/, false otherwise.
 */
function isFcForm(redirectUrl) {
  try {
    const currentHostname = window.location.hostname;
    const redirectHostname = new URL(redirectUrl).hostname;
    return currentHostname === redirectHostname && redirectUrl.includes("/form/provide/");
  } catch (e) {
    console.error("Error parsing redirect URL:", e);
    return false;
  }
}

/**
 * Submits a form with a POST request to the given URL within the IFRAME.
 * @param {string} url The URL to submit the form to.
 * @param {string} portalAlias The portal alias to use.
 */
function submitFormInIframe(url, portalAlias) {
  const iframe = document.querySelector(`iframe[name="${TriggerResponseFrame.name}"]`);
  if (iframe instanceof HTMLIFrameElement && iframe.contentDocument) {
    const form = document.createElement("form");

    form.action = appendParameterToUrl(url, "portal_alias", portalAlias);
    form.method = "POST";

    iframe.contentDocument.body.appendChild(form);
    form.submit();
  }
}

/**
 * Updates the form record row with the given UUID.
 * @param {string} formRecordUuid
 */
export async function updateFormRecordRow(formRecordUuid) {
  await callUpdateFormRecordRowRemoteCommand(formRecordUuid);
}

/**
 * Calls the remote command to update the form record row.
 * @param {string} formRecordUuid
 */
async function callUpdateFormRecordRowRemoteCommand(formRecordUuid) {
  await rcUpdateFormRecordRow?.([{ name: "formRecordUuid", value: formRecordUuid }]);
}

/**
 * When the user clicks on the back button, this function is called to switch back to the form records list.
 */
function callBackToFormRecordsRemoteCommand() {
  rcBackToFormRecords?.([]);
}

/**
 * When the user selects a form record, this function is called to select the form record
 * @param {string} formRecordUuid the UUID of the form record to select
 * @param {string} tabName the name of the tab to open. Can be "MESSAGE", "FILE" or "HISTORY".
 * @param {string} pushState if true, the state will be pushed to the history, otherwise the state will not be changed
 */
function callBackToFormRecordSelectRemoteCommand(formRecordUuid, tabName, pushState) {
  rcFormRecordRowSelect?.([
    { name: "formRecordUuid", value: formRecordUuid },
    { name: "tabName", value: tabName },
    { name: "pushState", value: pushState },
  ]);
}

/**
 *
 * @param {string} triggerName
 */
function callSuccessGrowlRemoteCommand(triggerName) {
  rcSendWorkflowTriggerSuccess?.([{ name: "triggerName", value: triggerName }]);
}

/**
 *
 * @param {string} triggerName
 */
function callErrorGrowlRemoteCommand(triggerName) {
  rcSendWorkflowTriggerError?.([{ name: "triggerName", value: triggerName }]);
}

/**
 *
 * @param {string} triggerUuid
 * @param {string} triggerName
 * @param {string} taskUuid
 * @param {string} formRecordUuid
 */
function callSendWorkflowTriggerParameters(triggerUuid, taskUuid, formRecordUuid, triggerName) {
  rcSendWorkflowTriggerParameter?.([
    { name: "triggerName", value: triggerName },
    {
      name: "triggerUuid",
      value: triggerUuid,
    },
    { name: "taskUuid", value: taskUuid },
    { name: "formRecordUuid", value: formRecordUuid },
  ]);
}
